import React from "react";
import { Box, Divider, Grid, Hidden, Link, Typography } from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import { useTranslation } from "react-i18next";
import { OrganizationInvoiceData } from "../../../../../models/organizationInvoiceData";
import { CancellationInvoiceListData } from "../../../../../models/cancellationInvoiceData";
import { Utils } from "../../../../../services/utils";

/**
 * @interface InvoiceListProps
 */
interface InvoiceListProps {
  organizationInvoiceData: OrganizationInvoiceData[] | CancellationInvoiceListData[];
  isCancellationInvoice?: boolean;
}

/**
 * Component for rendering a list of organization memberships.
 * @param {Object} props - Component props.
 * @param {Array} props.organizationInvoiceData - List of invoices.
 * @param {boolean} props.isCancellationInvoice - Flag whether type is cancellation invoice.
 * @returns {JSX.Element} - Rendered component.
 */
const InvoiceList = ({
  organizationInvoiceData,
  isCancellationInvoice = false,
}: InvoiceListProps): JSX.Element => {
  const { t } = useTranslation(["organization"]);

  return (
    <Box width="100%">
      <Grid container spacing={0} className="myinvoices-list-header">
        <Hidden mdDown>
          <Grid item xs={3} style={{ fontWeight: "bold" }}>
            {t("invoice.number", { ns: "organization" })}
          </Grid>
        </Hidden>
        <Hidden smDown>
          <Grid
            item
            xs={!isCancellationInvoice ? 3 : 6}
            style={{ textAlign: "center", fontWeight: "bold" }}
          >
            {t("invoice.date", { ns: "organization" })}
          </Grid>
          {!isCancellationInvoice && (
            <Grid item xs={3} style={{ textAlign: "center", fontWeight: "bold" }}>
              {t("invoice.dueDate", { ns: "organization" })}
            </Grid>
          )}
          <Grid item xs={3} style={{ textAlign: "center", fontWeight: "bold" }}>
            {t("download", { ns: "common" })}
          </Grid>
        </Hidden>
      </Grid>

      {organizationInvoiceData?.length > 0 ? (
        organizationInvoiceData?.map(
          (invoice: OrganizationInvoiceData | CancellationInvoiceListData) => {
            return (
              <React.Fragment key={`invoice-${invoice.id}`}>
                <Grid container spacing={0} className="myinvoices-list-item">
                  <Hidden mdDown>
                    <Grid item xs={3}>
                      {invoice.invoice_number}
                    </Grid>
                  </Hidden>
                  <Hidden mdDown>
                    <Grid
                      item
                      xs={!isCancellationInvoice ? 3 : 6}
                      style={{ textAlign: "center" }}
                    >
                      {Utils.formatDateToCetFormat(invoice.invoice_date)}
                    </Grid>
                    {!isCancellationInvoice && "due_date" in invoice && (
                      <Grid item xs={3} style={{ textAlign: "center" }}>
                        {invoice.due_date}
                      </Grid>
                    )}
                    <Grid item xs={3} style={{ textAlign: "center" }}>
                      <Link
                        href={invoice.file_name}
                        download={invoice.file_name}
                        target="_blank"
                        className="myinvoices-document-download-icon"
                      >
                        <GetAppIcon />
                      </Link>
                    </Grid>
                  </Hidden>
                </Grid>
                <Divider className="invoice-list-separator" />
              </React.Fragment>
            );
          }
        )
      ) : (
        <Typography>
          {!isCancellationInvoice
            ? t("invoice.noInvoice", { ns: "organization" })
            : t("invoice.noCancellationInvoice", { ns: "organization" })}
        </Typography>
      )}
    </Box>
  );
};

export default InvoiceList;
